import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { APP_TIMEZONE } from '@/shared/lib/dayjs';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import {
  AppAdminFormLabel,
  AppButton,
  AppCheckbox,
  AppForm,
  AppInputNumber,
  AppInputTime,
  AppSelect,
  AppTextField,
} from '@/components/atoms';
import { Building, MapMarker, User } from '@/components/atoms/AppIcon';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import { dateValueToIso } from '@/utils/dateConverter';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { useMassagerClinicLocations } from '@/hooks/massager/useMassagerClinicLocations';
import {
  useGpsPatientSearch,
  usePatientAddresses,
  usePatientSearch,
} from '@/hooks/massager/usePatientLookup';
import { useCreateVisit } from '@/hooks/massager/useVisitMutations';
import { precheckSuddenVisit } from '@/services/massager/visit.service';
import { useMassagerAuthContext } from '@shared/providers/MassagerAuthContext';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  massagerAddVisitFormSchema,
  type MassagerAddVisitFormData,
} from '@/schemas/massagerAddVisitSchema';
import { NO_PRINT_DATA_MESSAGE } from '@/pages/main/treatmentRecord/constants';
import type { PatientSearchResult } from '@/types/massager/visit';

interface AddVisitCardProps {
  targetDate: string; // YYYY-MM-DD
  initialLatitude?: number | null;
  initialLongitude?: number | null;
  /**
   * GPS-search trigger token. When this string changes (and lat/lng are
   * present), the card runs `searchByGps` against the backend and merges the
   * returned candidates into the patient dropdown — same UI as the by-name
   * flow. Each click of the page-level GPSで追加 button should bump this so
   * the effect re-fires even when coords are identical.
   */
  gpsTriggerKey?: string | null;
  /**
   * Page-level error from useGeolocation (denied / unavailable / timeout).
   * Surfaced inside the card on the same red-text line used for search info,
   * matching A2/R2 spec which renders the geolocation message on the form.
   */
  gpsErrorMessage?: string | null;
  existingVisits?: ReadonlyArray<{
    patient_id: number;
    massager_id: number | null;
    visit_datetime_from: string;
    visit_datetime_to: string;
  }>;
  onCreated?: () => void;
}

const CLINIC_LOCATION_PLACEHOLDER = 0;
const PATIENT_PLACEHOLDER = 0;

// Display order of validatable fields. The single banner above the patient
// picker shows the first non-empty error following this sequence so users
// see one message at a time matching the visual top-to-bottom layout.
const FIELD_ORDER: (keyof MassagerAddVisitFormData)[] = [
  'visit_time',
  'duration',
  'clinic_location_id',
  'patient_id',
  'medical_institution_name',
  'doctor_name',
  'sudden_reason',
  'coordination_date',
  'address_id',
];

// 突発的な往療 block: the 4 fields share one generic banner message
// (必須項目を入力して下さい。) instead of their field-specific zod messages.
const SUDDEN_FIELD_LIST = [
  'medical_institution_name',
  'doctor_name',
  'sudden_reason',
  'coordination_date',
] as const satisfies readonly (keyof MassagerAddVisitFormData)[];

const SUDDEN_FIELDS: ReadonlySet<keyof MassagerAddVisitFormData> = new Set(SUDDEN_FIELD_LIST);

const SUDDEN_BLOCK_MESSAGE = '必須項目を入力して下さい。';

const buildDefaults = (): MassagerAddVisitFormData => ({
  visit_time: dayjs().tz(APP_TIMEZONE).format('HH:mm'),
  duration: 20,
  clinic_location_id: undefined,
  // Default to the 「選択してください」 sentinel so the patient picker shows the
  // placeholder selected the moment the add-visit card is first opened (both
  // 受診者名で追加 and GPSで追加 flows). Schema treats 0 same as null, so
  // validation/effectivePatientId branches are unaffected.
  patient_id: PATIENT_PLACEHOLDER,
  address_id: undefined,
  no_treatment: false,
  is_sudden: false,
  medical_institution_name: '',
  doctor_name: '',
  sudden_reason: '',
  coordination_date: null,
});

const AddVisitCard: React.FC<AddVisitCardProps> = ({
  targetDate,
  initialLatitude = null,
  initialLongitude = null,
  gpsTriggerKey = null,
  gpsErrorMessage = null,
  existingVisits = [],
  onCreated,
}) => {
  const { serviceType, colors } = useTheme();
  const { clinic, massager } = useMassagerAuthContext();
  const currentMassagerId = massager?.id ?? null;
  const toast = useAppToast();
  const appModal = useAppModal();

  const themedServiceType =
    serviceType === SERVICE_TYPE_A2 || serviceType === SERVICE_TYPE_R2 ? serviceType : null;
  const visitServiceType =
    themedServiceType === SERVICE_TYPE_A2 || themedServiceType === SERVICE_TYPE_R2
      ? themedServiceType
      : undefined;

  const { locations, refetch: refetchLocations } = useMassagerClinicLocations({
    date: targetDate,
    serviceType: themedServiceType,
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    getValues,
    formState: { errors },
  } = useTrimmedForm<MassagerAddVisitFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(massagerAddVisitFormSchema),
    defaultValues: buildDefaults(),
  });

  const [keyword, setKeyword] = useState<string>('');
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchInfo, setSearchInfo] = useState<string | null>(null);
  // Track 連携日 calendar open state so we can add bottom padding to the form
  // while the popup is open — keeps the calendar fully visible above the
  // sticky footer on small viewports without permanently inflating the layout.
  const [coordinationPickerOpen, setCoordinationPickerOpen] = useState(false);
  // Unified candidate list — fed by either the name-search mutation or the
  // GPS-search mutation. Whichever search runs last wins (the dropdown is
  // single-source by design — A2/R2 blade only shows one set of options at a
  // time). Persisted across the parent's hide/show toggle so closing the
  // card with form data intact keeps the picked patient selectable.
  const [candidates, setCandidates] = useState<PatientSearchResult[]>([]);
  const searchMut = usePatientSearch();
  const gpsSearchMut = useGpsPatientSearch();
  const patientId = watch('patient_id');
  const clinicLocationId = watch('clinic_location_id');
  const isSudden = watch('is_sudden');
  const addressId = watch('address_id');

  const effectivePatientId =
    patientId == null || patientId === PATIENT_PLACEHOLDER ? null : patientId;

  const {
    addresses,
    hasElectricTreatment,
    suddenlyVisitIs,
    isFetching: isFetchingAddresses,
  } = usePatientAddresses(effectivePatientId, targetDate, themedServiceType);

  useEffect(() => {
    if (!addresses.length) return;
    const preferred = addresses.find(a => a.is_default) ?? addresses[0];
    setValue('address_id', preferred.id, { shouldValidate: false });
  }, [addresses, setValue]);

  useEffect(() => {
    if (effectivePatientId == null) {
      setValue('address_id', undefined, { shouldValidate: false });
    }
  }, [effectivePatientId, setValue]);

  useEffect(() => {
    if (
      clinicLocationId != null &&
      !locations.some(l => l.clinic_location_id === clinicLocationId)
    ) {
      setValue('clinic_location_id', undefined, { shouldValidate: false });
    }
  }, [locations, clinicLocationId, setValue]);

  // Checkbox hidden by default → only revealed when the picked patient has an
  // electric-treatment consent. Force-clear no_treatment whenever the checkbox
  // would be hidden so a stale tick can't leak into the create payload.
  useEffect(() => {
    if (!hasElectricTreatment) {
      setValue('no_treatment', false, { shouldValidate: false });
    }
  }, [hasElectricTreatment, setValue]);

  // Same gate for the 突発的な往療 checkbox — only shown when the patient has a
  // covering 同意書 with walking_condition_id = 2 (歩行困難). Backend computes
  // this into `suddenly_visit_is`. When false we also force-clear the form's
  // is_sudden + the 4 sudden fields so a previous toggle doesn't leak into
  // the payload.
  useEffect(() => {
    if (!suddenlyVisitIs) {
      setValue('is_sudden', false, { shouldValidate: false });
      setValue('medical_institution_name', '', { shouldValidate: false });
      setValue('doctor_name', '', { shouldValidate: false });
      setValue('sudden_reason', '', { shouldValidate: false });
      setValue('coordination_date', null, { shouldValidate: false });
      clearErrors([
        'medical_institution_name',
        'doctor_name',
        'sudden_reason',
        'coordination_date',
      ]);
    }
  }, [suddenlyVisitIs, setValue, clearErrors]);

  useEffect(() => {
    // Any keystroke clears EVERY RHF error — banner disappears until the
    // user submits again. Avoids the surprise where fixing field A would
    // immediately surface field B's pre-existing error in the banner.
    const subscription = watch((_, { name }) => {
      if (!name) return;
      clearErrors();
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const createMut = useCreateVisit();

  const locationOptions = useMemo(
    () => [
      { value: CLINIC_LOCATION_PLACEHOLDER, label: '選択してください' },
      ...locations.map(l => ({
        value: l.clinic_location_id,
        label: l.address ? `${l.location_name} ー ${l.address}` : l.location_name,
      })),
    ],
    [locations]
  );

  const patientOptions = useMemo(() => {
    const opts = candidates.map(p => ({
      value: p.patient_id,
      label: p.name,
    }));
    return [{ value: PATIENT_PLACEHOLDER, label: '選択してください' }, ...opts];
  }, [candidates]);

  const addressOptions = useMemo(
    () => addresses.map(a => ({ value: a.id, label: a.label })),
    [addresses]
  );

  const applyCandidates = (list: PatientSearchResult[]) => {
    setCandidates(list);
    if (list.length === 0) {
      // Spec: 該当するデータはありませんでした。 — same line as multi-result branch.
      setSearchInfo(NO_PRINT_DATA_MESSAGE);
      setValue('patient_id', undefined, { shouldValidate: false });
      return;
    }
    if (list.length === 1) {
      setValue('patient_id', list[0].patient_id, { shouldValidate: true });
      return;
    }
    setSearchInfo('複数の候補があります。選択してください。');
    setValue('patient_id', PATIENT_PLACEHOLDER, { shouldValidate: false });
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setSearchError('受診者仮名氏名を入力してください。');
      setSearchInfo(null);
      return;
    }
    setSearchError(null);
    setSearchInfo(null);
    const res = await searchMut.mutateAsync({ name: keyword.trim(), targetDate });
    applyCandidates(res.data?.patients ?? []);
    setKeyword('');
  };

  // GPS search trigger — the page bumps `gpsTriggerKey` when the user clicks
  // GPSで追加 and geolocation succeeds. We re-run the search even with
  // unchanged lat/lng (e.g. close → reopen) by keying off the token.
  const lastGpsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!gpsTriggerKey) return;
    if (lastGpsKeyRef.current === gpsTriggerKey) return;
    if (initialLatitude == null || initialLongitude == null) return;
    lastGpsKeyRef.current = gpsTriggerKey;

    setSearchError(null);
    setSearchInfo(null);
    void gpsSearchMut
      .mutateAsync({
        latitude: initialLatitude,
        longitude: initialLongitude,
        targetDate,
        serviceType: themedServiceType,
      })
      .then(res => applyCandidates(res.data?.patients ?? []))
      .catch(() => {
        // Backend failure — surface as the spec error line, mirroring how
        // 該当するデータはありませんでした is shown for the no-result branch.
        setCandidates([]);
        setSearchInfo('位置情報取得できませんでした。');
        setValue('patient_id', undefined, { shouldValidate: false });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsTriggerKey, initialLatitude, initialLongitude]);

  // Geolocation failure (denied / unavailable / timeout) bubbled up from the
  // page — render the message inline on the card per spec.
  useEffect(() => {
    if (!gpsErrorMessage) return;
    setCandidates([]);
    setSearchError(null);
    setSearchInfo(gpsErrorMessage);
    setValue('patient_id', undefined, { shouldValidate: false });
  }, [gpsErrorMessage, setValue]);

  const API_FIELD_MAP: Record<string, keyof MassagerAddVisitFormData> = {
    visit_datetime_from: 'visit_time',
    visit_minutes: 'duration',
    patient_id: 'patient_id',
    patient_address_id: 'address_id',
    clinic_location_id: 'clinic_location_id',
  };

  const applyOverlapCheck = (data: MassagerAddVisitFormData): boolean => {
    if (!data.visit_time || typeof data.duration !== 'number' || data.duration < 1) {
      return false;
    }
    if (!/^\d{2}:\d{2}$/.test(data.visit_time)) return false;
    // Compute start/end in JST so the overlap window aligns with the
    // backend's APP_TIMEZONE=Asia/Tokyo, regardless of browser TZ.
    const startMs = dayjs.tz(`${targetDate} ${data.visit_time}:00`, APP_TIMEZONE).valueOf();
    const endMs = startMs + data.duration * 60_000;
    const overlap = existingVisits.some(v => {
      const vStart = dayjs(v.visit_datetime_from);
      const vEnd = dayjs(v.visit_datetime_to);
      if (!vStart.isValid() || !vEnd.isValid()) return false;
      return startMs < vEnd.valueOf() && endMs > vStart.valueOf();
    });
    if (!overlap) return false;
    const overlapMessage = '指定された訪問日時には別の受診者がすでに登録されています。';
    setError('visit_time', { type: 'overlap', message: overlapMessage });
    setError('duration', { type: 'overlap', message: overlapMessage });
    return true;
  };

  const onValid = async (data: MassagerAddVisitFormData) => {
    clearErrors();
    setSearchError(null);

    // superRefine guarantees these are present once we reach onValid; assert
    // the narrow types so the create payload matches StoreVisitPayload.
    if (data.patient_id == null || data.duration == null) return;

    const visitDatetimeFrom = `${targetDate} ${data.visit_time}:00`;

    // Overlap check on submit (zod-valid path). See applyOverlapCheck below.
    if (applyOverlapCheck(data)) return;

    // Check if the patient is already registered for this date.
    const sameMassagerHit = existingVisits.some(
      v => v.patient_id === data.patient_id && v.massager_id === currentMassagerId
    );
    if (sameMassagerHit) {
      setSearchInfo('この受診者はすでに登録されています。');
      return;
    }

    // Pre-flight clinic-location validity: the dropdown shows the list as
    // it was at mount time. Between then and submit, the chosen location
    // could have been soft-deleted (clinic_locations.deleted_at) or the
    // massager could have been removed from CLM (clinic_location_massagers
    // .deleted_at). Refetch the active list and reject when our id is gone.
    if (data.clinic_location_id != null) {
      const refetched = await refetchLocations();
      const freshList = refetched.data?.data?.clinic_locations ?? [];
      const stillValid = freshList.some(l => l.clinic_location_id === data.clinic_location_id);
      if (!stillValid) {
        toast.error('指定された施設は存在しません。他の施設を選んでください。');
        const firstId = freshList[0]?.clinic_location_id;
        setValue('clinic_location_id', firstId ?? undefined, { shouldValidate: false });
        return;
      }
    }

    // 突発的な往療 BD validation:
    //   block — patient already has a sudden visit within ±14 days (any massager).
    //   warn  — at least one sudden visit already exists in the same calendar
    //           month (must hand-write rezept extras). Confirming proceeds.
    if (data.is_sudden) {
      let pre;
      try {
        pre = await precheckSuddenVisit(clinic?.site_type ?? '', {
          patient_id: data.patient_id,
          visit_datetime_from: visitDatetimeFrom,
          service_type:
            themedServiceType === SERVICE_TYPE_A2 || themedServiceType === SERVICE_TYPE_R2
              ? themedServiceType
              : undefined,
        });
      } catch (err) {
        toast.error(getApiErrorMessage(err, 'チェックに失敗しました。'));
        return;
      }

      const result = pre.data;
      if (result.blocked && result.block_message) {
        appModal.error({
          type: 'error',
          content: <div className="whitespace-pre-line">{result.block_message}</div>,
          okText: 'OK',
        });
        return;
      }
      if (result.warn_message) {
        const confirmed = await new Promise<boolean>(resolve => {
          appModal.warning({
            type: 'warning',
            content: <div className="whitespace-pre-line">{result.warn_message}</div>,
            okText: 'OK',
            onOk: () => resolve(true),
            afterClose: () => resolve(false),
          });
        });
        if (!confirmed) return;
      }
    }

    createMut.mutate(
      {
        latitude: initialLatitude,
        longitude: initialLongitude,
        clinic_location_id: data.clinic_location_id ?? null,
        patient_id: data.patient_id,
        patient_address_id: data.address_id,
        visit_datetime_from: visitDatetimeFrom,
        visit_minutes: data.duration,
        // FE always sends the R2-shaped 「電療無し」 boolean. Backend reads
        // service_type and translates: R2 → r_visits.is_not_hot_treatment,
        // A2 → visits.electric_treatment (inverted, 0 = 無し).
        is_not_hot_treatment: data.no_treatment,
        service_type: visitServiceType,
        is_sudden: data.is_sudden,
        hospital_name: data.is_sudden ? data.medical_institution_name : null,
        doctor_name: data.is_sudden ? data.doctor_name : null,
        sudden_reason: data.is_sudden ? data.sudden_reason : null,
        coordination_date:
          data.is_sudden && data.coordination_date
            ? data.coordination_date.format('YYYY-MM-DD')
            : null,
      },
      {
        onSuccess: () => {
          setKeyword('');
          setCandidates([]);
          setSearchInfo(null);
          searchMut.reset();
          gpsSearchMut.reset();
          lastGpsKeyRef.current = null;
          reset(buildDefaults());
          onCreated?.();
        },
        onError: err => {
          const apiErrors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
            .response?.data?.errors;
          if (!apiErrors) return;

          // Handle patient_id error with searchInfo like the old pre-check
          if (apiErrors.patient_id) {
            const msg = apiErrors.patient_id[0];
            setSearchInfo(msg);
          }

          Object.entries(apiErrors).forEach(([key, messages]) => {
            const field = API_FIELD_MAP[key];
            const message = Array.isArray(messages) ? messages[0] : undefined;
            if (field && message && key !== 'patient_id') {
              setError(field, { type: 'server', message });
            }
          });
        },
      }
    );
  };

  const onInvalid = () => {
    // Submitting the form supersedes any stale 受診者名検索欄 error so the
    // shared banner can switch to the first RHF field error.
    setSearchError(null);
    // Even when zod blocks submit, still surface the time-overlap error so
    // the user sees it alongside the missing-field errors instead of having
    // to fix everything else first and then discover the overlap.
    applyOverlapCheck(getValues());
  };

  // The form surfaces one error at a time: the first field in FIELD_ORDER
  // that has a non-empty message owns both the banner text AND the red
  // border. Other erroring fields render plain until the user clears this
  // one — keeps the form visually calm instead of lighting up every input.
  // Not memoized: RHF can mutate the `errors` object in place without
  // changing its reference, which would leave a useMemo result stale and
  // freeze the banner/border on a field the user has already fixed.
  let firstErrorField: keyof MassagerAddVisitFormData | null = null;
  for (const key of FIELD_ORDER) {
    const msg = errors[key]?.message;
    if (typeof msg === 'string' && msg.length > 0) {
      firstErrorField = key;
      break;
    }
  }

  const firstFieldErrorMessage = firstErrorField
    ? SUDDEN_FIELDS.has(firstErrorField)
      ? SUDDEN_BLOCK_MESSAGE
      : (errors[firstErrorField]?.message ?? null)
    : null;

  // Priority: 受診者名検索欄 input-level error (immediate 検索 action) → first
  // RHF field error → search info. searchError is cleared when the user
  // submits the form (see onInvalid) so RHF errors can take over after that.
  const isSearchErrorActive = !!searchError;
  const bannerMessage = searchError ?? firstFieldErrorMessage ?? searchInfo;

  const isFirstError = (name: keyof MassagerAddVisitFormData) => firstErrorField === name;

  // Clear EVERY RHF error — invoked from each sudden input's onChange so
  // typing wipes the whole banner state until the next submit. Helper
  // name kept for the existing call-sites; semantics now match the
  // "no auto-cascade" rule applied form-wide.
  const clearSuddenErrors = () => clearErrors();

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="text-base font-semibold text-gray-900">訪問追加</h3>
      </div>

      <form
        onSubmit={handleSubmit(onValid, onInvalid)}
        className={`flex flex-col gap-4 p-4 ${coordinationPickerOpen ? 'pb-40' : ''}`}
        noValidate
      >
        {/* Time + duration — labels stacked above each input, two columns
            kept side-by-side on every viewport (per HIT-2329 spec). */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <Controller
            name="visit_time"
            control={control}
            render={({ field }) => (
              <div className="flex min-w-0 flex-col gap-1">
                <AppAdminFormLabel label="訪問時間" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('visit_time') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <AppInputTime
                    id="visit-time"
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={isFirstError('visit_time')}
                    focusColor={colors.primary}
                  />
                </AppForm.Item>
              </div>
            )}
          />
          <Controller
            name="duration"
            control={control}
            render={({ field }) => (
              <div className="flex min-w-0 flex-col gap-1">
                <AppAdminFormLabel label="所要時間" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('duration') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <div className="flex items-center gap-2">
                    <AppInputNumber
                      id="visit-duration"
                      min={0}
                      max={999}
                      precision={0}
                      controls={false}
                      value={field.value ?? null}
                      onChange={v => {
                        if (v == null || v === '') {
                          field.onChange(null);
                          return;
                        }
                        const n = typeof v === 'number' ? v : Number(v);
                        field.onChange(Number.isFinite(n) ? n : null);
                      }}
                      onBlur={field.onBlur}
                      className="!min-w-0 !flex-1"
                    />
                    <span className="shrink-0 text-sm text-gray-600">分</span>
                  </div>
                </AppForm.Item>
              </div>
            )}
          />
        </div>

        {/* Clinic location (required) */}
        <Controller
          name="clinic_location_id"
          control={control}
          render={({ field }) => (
            <div className="rounded-md border border-purple-200 bg-purple-50 px-3 py-3">
              <div className="mb-1 flex items-center gap-2 text-purple-700">
                <Building />
                <AppAdminFormLabel label="施設" required className="!mb-0" />
              </div>
              <AppForm.Item
                validateStatus={isFirstError('clinic_location_id') ? 'error' : undefined}
                className="!mb-0"
              >
                <AppSelect
                  id="visit-clinic-location"
                  value={field.value ?? CLINIC_LOCATION_PLACEHOLDER}
                  onChange={v => {
                    if (v == null) {
                      field.onChange(undefined);
                      return;
                    }
                    const num = typeof v === 'number' ? v : Number(v);
                    field.onChange(num === CLINIC_LOCATION_PLACEHOLDER ? undefined : num);
                  }}
                  onBlur={field.onBlur}
                  options={locationOptions}
                  placeholder=""
                  style={{ width: '100%' }}
                  allowClear
                />
              </AppForm.Item>
            </div>
          )}
        />

        {/* Patient search (not part of the form payload — drives patient_id below) */}
        <div className="flex flex-col gap-1">
          <AppAdminFormLabel label="受診者名検索欄" required className="!mb-0" />
          <div className="flex gap-2">
            <AppForm.Item
              validateStatus={isSearchErrorActive ? 'error' : undefined}
              className="!mb-0 !flex-1"
            >
              <AppTextField
                id="patient-search"
                value={keyword}
                onChange={e => {
                  setKeyword(e.target.value);
                  if (searchError) setSearchError(null);
                  if (searchInfo) setSearchInfo(null);
                }}
                onPressEnter={handleSearch}
                maxLength={255}
              />
            </AppForm.Item>
            <AppButton type="primary" onClick={handleSearch} loading={searchMut.isPending}>
              検索
            </AppButton>
          </div>
        </div>

        {bannerMessage && (
          <p className="mt-2 whitespace-pre-line text-base font-medium text-red-500">
            {bannerMessage}
          </p>
        )}

        {/* Patient picker */}
        <Controller
          name="patient_id"
          control={control}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <User />
                </span>
                <AppForm.Item
                  validateStatus={isFirstError('patient_id') ? 'error' : undefined}
                  className="!mb-0 !flex-1"
                >
                  <AppSelect
                    value={field.value ?? undefined}
                    onChange={v =>
                      field.onChange(v == null ? undefined : typeof v === 'number' ? v : Number(v))
                    }
                    onBlur={field.onBlur}
                    placeholder=""
                    options={patientOptions}
                    style={{ width: '100%' }}
                  />
                </AppForm.Item>
              </div>
            </div>
          )}
        />

        {/* Treatment / sudden flags */}
        <div className="flex flex-col items-start gap-2">
          {hasElectricTreatment && (
            <Controller
              name="no_treatment"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                  {themedServiceType === SERVICE_TYPE_A2 ? '電(鍼・温)無し' : '温罨法・電療無し'}
                </AppCheckbox>
              )}
            />
          )}
          {suddenlyVisitIs && (
            <Controller
              name="is_sudden"
              control={control}
              render={({ field }) => (
                <AppCheckbox checked={field.value} onChange={e => field.onChange(e.target.checked)}>
                  突発的な往療
                </AppCheckbox>
              )}
            />
          )}
        </div>

        {/* Sudden-visit details. Per-field error messages bubble up to the
            single banner above the patient picker; only the red border on
            each input is shown locally. */}
        {isSudden && (
          <div className="flex flex-col gap-3 rounded-md bg-gray-50 p-3">
            <Controller
              name="medical_institution_name"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <AppAdminFormLabel label="医療機関名" required className="!mb-0" />
                  <AppForm.Item
                    validateStatus={isFirstError('medical_institution_name') ? 'error' : undefined}
                    className="!mb-0"
                  >
                    <AppTextField
                      id="medical-institution-name"
                      value={field.value}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      onChange={e => {
                        field.onChange(e);
                        clearSuddenErrors();
                      }}
                      maxLength={50}
                    />
                  </AppForm.Item>
                </div>
              )}
            />
            <Controller
              name="doctor_name"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <AppAdminFormLabel label="医師名" required className="!mb-0" />
                  <AppForm.Item
                    validateStatus={isFirstError('doctor_name') ? 'error' : undefined}
                    className="!mb-0"
                  >
                    <AppTextField
                      id="doctor-name"
                      value={field.value}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      onChange={e => {
                        field.onChange(e);
                        clearSuddenErrors();
                      }}
                      maxLength={50}
                    />
                  </AppForm.Item>
                </div>
              )}
            />
            <Controller
              name="sudden_reason"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <AppAdminFormLabel label="突発往療理由" required className="!mb-0" />
                  <AppForm.Item
                    validateStatus={isFirstError('sudden_reason') ? 'error' : undefined}
                    className="!mb-0"
                  >
                    <AppTextField
                      id="sudden-reason"
                      value={field.value}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      onChange={e => {
                        field.onChange(e);
                        clearSuddenErrors();
                      }}
                      maxLength={50}
                    />
                  </AppForm.Item>
                </div>
              )}
            />
            <Controller
              name="coordination_date"
              control={control}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <AppAdminFormLabel label="連携日" required className="!mb-0" />
                  <AppForm.Item
                    validateStatus={isFirstError('coordination_date') ? 'error' : undefined}
                    className="!mb-0"
                  >
                    <RA2NDatePicker
                      value={field.value ? field.value.format('YYYY-MM-DD') : ''}
                      onChange={v => {
                        const iso = dateValueToIso(v);
                        field.onChange(iso ? dayjs(iso) : null);
                        clearSuddenErrors();
                      }}
                      className="w-full"
                      error={isFirstError('coordination_date')}
                      placeholder="元号00年00月00日"
                      onOpenChange={setCoordinationPickerOpen}
                    />
                  </AppForm.Item>
                </div>
              )}
            />
            <p className="text-sm text-red-500">
              同意書交付の医師以外の場合、その理由をレセプト摘要欄に手書きで記載ください。
            </p>
          </div>
        )}

        {/* Address picker */}
        <Controller
          name="address_id"
          control={control}
          render={({ field }) => {
            return (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center text-lg text-emerald-600">
                    <MapMarker />
                  </span>
                  <AppForm.Item
                    validateStatus={isFirstError('address_id') ? 'error' : undefined}
                    className="!mb-0 !flex-1"
                  >
                    <AppSelect
                      value={addressOptions.length > 0 ? (field.value ?? undefined) : undefined}
                      onChange={v =>
                        field.onChange(
                          v == null ? undefined : typeof v === 'number' ? v : Number(v)
                        )
                      }
                      onBlur={field.onBlur}
                      options={addressOptions}
                      loading={isFetchingAddresses}
                      style={{ width: '100%' }}
                      placeholder=""
                    />
                  </AppForm.Item>
                </div>
              </div>
            );
          }}
        />

        {/* 特別地域加算 — red statutory notice (法改正 2024-06).
            Resolved from the picked address: id=0 (base) → PI flag, else
            PatientAddress flag. Surfaces just above the submit button. */}
        {(() => {
          if (addressId == null) return null;
          const picked = addresses.find(a => a.id === addressId);
          if (!picked?.is_special_area_addition) return null;
          return <p className="text-base font-medium text-red-500">特別地域加算</p>;
        })()}

        {/* Submit */}
        <AppButton
          block
          type="primary"
          size="large"
          htmlType="submit"
          loading={createMut.isPending}
          className="!min-h-11 !bg-emerald-600 hover:!bg-emerald-700"
        >
          訪問追加
        </AppButton>
      </form>
    </div>
  );
};

export default AddVisitCard;
